/** Read expected delivery time from a normalized store order row. */
export function expectedDeliveryTimeFromOrder(order) {
  if (!order || typeof order !== 'object') return null;
  return (
    order.expectedDeliveryTime ??
    order.Expected_Delivery_Time ??
    order.expected_delivery_time ??
    null
  );
}

/** Parse API / UI datetime strings into a local Date. */
export function parseStoreOrderDateTime(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  const normalized = s.includes(' ') && !s.includes('T') ? s.replace(' ', 'T') : s;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Read created timestamp from a normalized order row. */
export function orderCreatedDateRaw(order) {
  if (!order || typeof order !== 'object') return '';
  const raw =
    order.createdDate ??
    order.createdAt ??
    order.CreatedDate ??
    order.created_date;
  return raw != null ? String(raw).trim() : '';
}

/** True when order created_date falls on the current local calendar day. */
export function isOrderCreatedToday(order) {
  const d = parseStoreOrderDateTime(orderCreatedDateRaw(order));
  if (!d) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** Display store order datetimes as DD/MM/YYYY HH:mm (always includes date + time). */
export function formatStoreOrderDateTime(raw) {
  const d = parseStoreOrderDateTime(raw);
  if (!d) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/** API datetime string from datetime-local value or ISO-ish input. */
export function formatExpectedDeliveryTimeForApi(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s || s.toUpperCase() === 'N/A') return null;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
  const d = parseStoreOrderDateTime(s.includes('T') ? s : s.replace(' ', 'T'));
  if (!d) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Value for input[type=datetime-local] from API datetime (local timezone). */
export function toDatetimeLocalInputValue(apiFormatted) {
  const parsed = parseStoreOrderDateTime(apiFormatted);
  if (!parsed) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

/** Value for input[type=date] from API datetime (local timezone). */
export function toDateInputValue(apiFormatted) {
  const parsed = parseStoreOrderDateTime(apiFormatted);
  if (!parsed) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/** Value for input[type=time] from API datetime (local timezone). */
export function toTimeInputValue(apiFormatted) {
  const parsed = parseStoreOrderDateTime(apiFormatted);
  if (!parsed) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

export function joinDateAndTimeLocal(date, time) {
  if (!date || !time) return '';
  return `${date}T${time}`;
}

export function parseTimeParts(time) {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
  return {
    hour: Number.parseInt(time.slice(0, 2), 10),
    minute: Number.parseInt(time.slice(3, 5), 10),
  };
}

export function formatTimeParts(hour, minute) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hour)}:${pad(minute)}`;
}

export function isTimePartBeforeMin(hour, minute, minTime) {
  if (!minTime) return false;
  const min = parseTimeParts(minTime);
  if (!min) return false;
  if (hour < min.hour) return true;
  if (hour > min.hour) return false;
  return minute < min.minute;
}

export function expectedDeliveryMinDateValue(createdRaw) {
  return toDateInputValue(createdRaw);
}

/** Earliest allowed time when selected date is the order created date. */
export function expectedDeliveryMinTimeValue(createdRaw, selectedDate) {
  if (!selectedDate) return '';
  const minDate = expectedDeliveryMinDateValue(createdRaw);
  if (!minDate || selectedDate !== minDate) return '';
  return toTimeInputValue(createdRaw);
}

export function clampExpectedDeliveryParts(date, time, createdRaw) {
  const minDate = expectedDeliveryMinDateValue(createdRaw);
  let nextDate = date || '';
  let nextTime = time || '';
  if (minDate && nextDate && nextDate < minDate) {
    nextDate = minDate;
  }
  const minTime = expectedDeliveryMinTimeValue(createdRaw, nextDate);
  if (minTime && nextTime && nextTime < minTime) {
    nextTime = minTime;
  }
  if (nextDate && !nextTime && minTime) {
    nextTime = minTime;
  }
  return { date: nextDate, time: nextTime };
}

export function formatExpectedDeliveryDisplay(raw) {
  return formatStoreOrderDateTime(raw);
}

/** True when expected delivery is strictly before order created time. */
export function isExpectedDeliveryBeforeCreated(expectedRaw, createdRaw) {
  const expected = parseStoreOrderDateTime(expectedRaw);
  const created = parseStoreOrderDateTime(createdRaw);
  if (!expected || !created) return false;
  return expected.getTime() < created.getTime();
}

/** Expected delivery only when it is on or after order created time. */
export function getDisplayableExpectedDeliveryTime(order) {
  const raw = expectedDeliveryTimeFromOrder(order);
  if (!raw || !parseStoreOrderDateTime(raw)) return null;
  const created = order?.createdDate ?? order?.Created_Date ?? null;
  if (isExpectedDeliveryBeforeCreated(raw, created)) return null;
  return raw;
}

export function formatDisplayableExpectedDelivery(order) {
  const raw = getDisplayableExpectedDeliveryTime(order);
  if (!raw) return '—';
  return formatExpectedDeliveryDisplay(raw);
}
